import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import Icon from '../../../hive/ui/Icon';
import { useFairTracker } from '../data/FairTrackerStore';
import { Field, Input, Select, Modal, Confirm, Empty, useToast } from '../ui';
import { newFair } from '../data/entities';
import { FAIR_STAGES } from '../data/constants';
import { supplierDecision, planProgress } from '../rules';
import { BASE } from '../routes';
import { tr } from '../i18n';

/**
 * Fairs hub (spec § 5.2) — the landing screen for every new app session.
 *
 * One card per fair with a gold left stripe (green when complete, grey when
 * upcoming) and the SUPPLIERS / STRONG / SHORTLIST counters. Deleting a fair
 * deletes its suppliers, so the confirm says so explicitly.
 */
export default function FairsScreen() {
  const history = useHistory();
  const toast = useToast();
  const { fairs, suppliers, settings, saveFair, deleteFair, setCurrentFair } = useFairTracker();

  const [editing, setEditing] = useState(null);
  const [confirming, setConfirming] = useState(null);

  const firstName = ((settings && settings.profile && settings.profile.name) || '').split(' ')[0];

  const open = async (id) => {
    await setCurrentFair(id);
    history.push(BASE);
  };

  const openAll = async () => {
    await setCurrentFair('all');
    history.push(BASE);
  };

  const save = async (fair) => {
    try {
      await saveFair(fair);
      setEditing(null);
      toast('Fair saved');
    } catch (e) {
      toast('Could not save', 'error');
    }
  };

  const remove = async () => {
    const fair = confirming;
    setConfirming(null);
    try {
      const res = await deleteFair(fair.id);
      toast(res && res.suppliers ? `Deleted with ${res.suppliers} suppliers` : 'Fair deleted');
    } catch (e) {
      toast('Could not delete', 'error');
    }
  };

  return (
    <div className="ft-page ft-page-bare">
      <section className="ft-hero">
        <h1 className="ft-hero-h1">{firstName ? tr(`Welcome back, ${firstName}.`) : tr('Your fairs.')}</h1>
        <p className="ft-hero-lead">
          {tr("Pick a fair to work in. Everything you capture is scoped to the fair you open.")}
        </p>
      </section>

      {!fairs.length && (
        <Empty title={tr("No fairs yet")}>{tr("Create one and Quick Entry is ready to use.")}</Empty>
      )}

      <div className="ft-list">
        {fairs.map((fair) => {
          const mine = suppliers.filter((s) => s.fairId === fair.id);
          const strong = mine.filter((s) => supplierDecision(s) === 'Strong').length;
          const shortlisted = mine.filter((s) => s.inShortlist).length;
          const plan = planProgress(fair);
          const stage = FAIR_STAGES.find((s) => s.value === (fair.status || 'active'));
          const stripe =
            fair.status === 'complete' ? 'green' : fair.status === 'upcoming' ? 'grey' : 'gold';

          return (
            <article className={`ft-faircard ft-stripe-${stripe}`} key={fair.id}>
              <header className="ft-faircard-head">
                <div>
                  <h2 className="ft-h2">{fair.name || 'Untitled fair'}</h2>
                  <p className="ft-sub" style={{ margin: 0 }}>
                    {[fair.location, fair.dates].filter(Boolean).join(' · ') || tr('No dates set')}
                  </p>
                </div>
                <span className={`ft-pill ft-pill-${stripe === 'green' ? 'strong' : stripe === 'grey' ? 'info' : 'maybe'}`}>
                  {stage ? tr(stage.label) : 'At the fair'}
                </span>
              </header>

              <dl className="ft-counters">
                <div>
                  <dt>{tr("Suppliers")}</dt>
                  <dd>{mine.length}</dd>
                </div>
                <div>
                  <dt>{tr("Strong")}</dt>
                  <dd>{strong}</dd>
                </div>
                <div>
                  <dt>{tr("Shortlist")}</dt>
                  <dd>{shortlisted}</dd>
                </div>
                {plan.total > 0 && (
                  <div>
                    <dt>{tr("Plan")}</dt>
                    <dd>
                      {plan.found}/{plan.total}
                    </dd>
                  </div>
                )}
              </dl>

              <div className="ft-faircard-actions">
                <button type="button" className="ft-btn ft-btn-ink" onClick={() => open(fair.id)}>
                  {tr("Open fair →")}
                </button>
                <button type="button" className="ft-btn" onClick={() => setEditing(fair)}>
                  {tr("Edit")}
                </button>
                <button type="button" className="ft-btn ft-btn-quiet" onClick={() => setConfirming(fair)}>
                  <Icon name="Trash2" size={15} /> {tr("Delete")}
                </button>
              </div>
            </article>
          );
        })}
      </div>

      <button
        type="button"
        className="ft-btn ft-btn-gold ft-btn-block"
        style={{ marginTop: 14 }}
        onClick={() => setEditing(newFair({ name: '' }))}
      >
        <Icon name="Plus" size={17} /> {tr("New fair")}
      </button>

      {fairs.length > 1 && (
        <button type="button" className="ft-btn ft-btn-block" style={{ marginTop: 8 }} onClick={openAll}>
          {tr("View all fairs together")}
        </button>
      )}

      {editing && <EditFair fair={editing} onSave={save} onClose={() => setEditing(null)} />}

      {confirming && (
        <Confirm
          title={`Delete ${confirming.name || 'this fair'}?`}
          /* The cascade count is the whole point of this dialog, so it has to
             read cleanly. "the 0 supplier(s) captured at it" was both noise
             (there is nothing to cascade) and awkward at every count. */
          message={(() => {
            const n = suppliers.filter((s) => s.fairId === confirming.id).length;
            if (!n) return tr('This cannot be undone.');
            const what = n === 1 ? tr('1 supplier') : `${n} ${tr('suppliers')}`;
            return `${tr('This also deletes the')} ${what} ${tr('captured at it. This cannot be undone.')}`;
          })()}
          confirmLabel={tr("Delete fair")}
          onConfirm={remove}
          onCancel={() => setConfirming(null)}
        />
      )}
    </div>
  );
}

/** Edit-fair modal (spec § 5.2): stage, FX, budget and the forwarder. */
function EditFair({ fair, onSave, onClose }) {
  const [f, setF] = useState(() => ({
    ...fair,
    fx: { ...(fair.fx || {}) },
    forwarder: { ...(fair.forwarder || {}) },
  }));

  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const setFx = (k) => (v) => setF((x) => ({ ...x, fx: { ...x.fx, [k]: v } }));
  const setFwd = (k) => (v) => setF((x) => ({ ...x, forwarder: { ...x.forwarder, [k]: v } }));

  return (
    <Modal
      title={fair.name ? 'Edit fair' : 'New fair'}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="ft-btn ft-btn-cancel" onClick={onClose}>
            {tr("Cancel")}
          </button>
          <button type="button" className="ft-btn ft-btn-green" onClick={() => onSave(f)}>
            {tr("Save fair")}
          </button>
        </>
      }
    >
      <Field label={tr("Fair name")}>
        <Input value={f.name} onChange={set('name')} placeholder="Canton Fair" />
      </Field>
      <div className="ft-grid-2">
        <Field label={tr("City / country")}>
          <Input value={f.location} onChange={set('location')} />
        </Field>
        <Field label={tr("Dates")}>
          <Input value={f.dates} onChange={set('dates')} />
        </Field>
      </div>
      <Field label={tr("Stage")} hint={tr("Completing the fair switches the Shortlist into review mode.")}>
        <Select value={f.status || 'active'} options={FAIR_STAGES} onChange={set('status')} />
      </Field>

      <h3 className="ft-h2" style={{ marginTop: 14 }}>
        {tr("Exchange rates")}
      </h3>
      <p className="ft-sub">
        {tr("Set once per fair. There is no live rate — these are what every USD figure uses.")}
      </p>
      <div className="ft-grid-2">
        <Field label={tr("1 CNY = USD")}>
          <Input value={f.fx.CNY || ''} onChange={setFx('CNY')} inputMode="decimal" />
        </Field>
        <Field label={tr("1 EUR = USD")}>
          <Input value={f.fx.EUR || ''} onChange={setFx('EUR')} inputMode="decimal" />
        </Field>
      </div>
      <Field label={tr("Buying budget (USD)")} hint={tr("Shown against what you have paid at booths.")}>
        <Input value={f.budget || ''} onChange={set('budget')} inputMode="decimal" />
      </Field>

      <h3 className="ft-h2" style={{ marginTop: 14 }}>
        {tr("Freight forwarder in China")}
      </h3>
      <p className="ft-sub">{tr("Shown on your supplier card so samples can be shipped there.")}</p>
      <Field label={tr("Name")}>
        <Input value={f.forwarder.name || ''} onChange={setFwd('name')} />
      </Field>
      <div className="ft-grid-2">
        <Field label={tr("Phone")}>
          <Input value={f.forwarder.phone || ''} onChange={setFwd('phone')} type="tel" />
        </Field>
        <Field label="WeChat">
          <Input value={f.forwarder.wechat || ''} onChange={setFwd('wechat')} />
        </Field>
      </div>
      <Field label={tr("Chinese address")} hint={tr("Shown large on the card — this is what the supplier reads.")}>
        <Input value={f.forwarder.addressCN || ''} onChange={setFwd('addressCN')} />
      </Field>
      <Field label={tr("English address")}>
        <Input value={f.forwarder.addressEN || ''} onChange={setFwd('addressEN')} />
      </Field>
    </Modal>
  );
}
